import { MemberProfile } from "@/services/core-api-adapter";
import {
  isFeatureEnabled,
  FeatureToggleIdentifier,
} from "@/services/feature-toggle-adapter";
import theme, { shadows } from "@/theme";
import { convertPxToRem } from "@/utils";
import { Box, Stack, Typography } from "@mui/material";
import MedicalAssistanceCTA from "../MedicalAssistanceCTA";
import MessagesIconCTA from "../MessagesIconCTA";
import {
  HealthCheckTile,
  BenefitsTile,
  HealthProfileTile,
  MyPeopleTile,
} from "../DashboardTiles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LifestyleAndAdviceSection from "../LifestyleAndAdviceSection";
import MembershipDashboardPanel from "@/features/Dashboard/components/MembershipDashboardPanel";
import useGetMemberships from "@/features/Common/hooks/useGetMemberships";
import { trackEvent, trackUserInteraction } from "@/services/analytics-adapter";
import ProfileIconCTA from "../ProfileIconCTA";
import AdditionalPayGServicesSection from "../AdditionalPayGServicesV2";
import PersonalizedPanel, {
  PersonalizedPanelProps,
} from "@/features/HealthScore/components/PersonalizedPanel/PersonalisedPanel";

export function getPersonalizedPanelPropsFromProfile(
  memberProfile: MemberProfile | null
): PersonalizedPanelProps {
  if (!memberProfile?.healthProfile) return {};
  const hp = memberProfile.healthProfile;
  const lastUpdated =
    hp.lastHealthScoreCalculationDate || hp.lastUpdatedAt
      ? new Date(hp.lastHealthScoreCalculationDate || hp.lastUpdatedAt)
          .toISOString()
          .slice(0, 10)
      : undefined;
  const diff = hp.healthScoreDifference ?? 0;
  const direction = hp.healthScoreDirection;
  const change =
    diff !== 0 && (direction === "increase" || direction === "decrease")
      ? {
          points: Math.abs(diff),
          direction: direction as "increase" | "decrease",
          timeFrameKey: "PersonalizedHealth.card.timeFrame.fromLastWeek",
        }
      : undefined;
  return {
    healthScore: hp.healthScoreValue,
    lastUpdated,
    change,
  };
}

interface B2BMemberDashboardProps {
  memberProfile: MemberProfile | null;
  isMemberProfileLoading: boolean;
  memberProfileFetchError: boolean;
}
export default function B2BMemberDashboard({
  memberProfile,
  isMemberProfileLoading,
  memberProfileFetchError,
}: B2BMemberDashboardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    firstMembership,
    memberships,
    membershipsFetchError,
    isMembershipsLoading,
  } = useGetMemberships();

  function onBellIconButtonClick() {
    navigate("/home/messages");
  }

  function onProfileIconCTAClick() {
    trackEvent({
      event: "action.myProfileCTAClicked",
      source: "home screen",
    });

    trackUserInteraction({
      linkText: "Home screen | My profile",
      linkIntent: "navigational",
      linkScope: "button",
    });

    navigate(`/home/memberships/${memberProfile?.membership.id}`);
  }

  return (
    <Stack spacing={2}>
      <Box
        p={2}
        gap={2}
        sx={{
          borderRadius: `0 0 ${convertPxToRem(15)}  ${convertPxToRem(15)}`,
          background: `linear-gradient(to bottom, ${theme.palette.primary.contrastText}, ${theme.palette.accent2.light}, ${theme.palette.accent2.light})`,
          width: "100%",
          height: convertPxToRem(170),
          boxShadow: shadows.light,
          position: "absolute",
          left: 0,
          top: 0,
          zIndex: 1,
        }}
      />
      <Stack sx={{ zIndex: 2, pt: 0, pb: 0 }} spacing={1} p={2}>
        <Stack direction="row" width="100%" justifyContent="end" spacing={1}>
          <Stack display="flex" alignItems="end" position="relative">
            <MessagesIconCTA onClick={onBellIconButtonClick} />
          </Stack>
          {isFeatureEnabled(FeatureToggleIdentifier.ENABLE_UNIFIED_CARE) && (
            <Stack
              sx={{ pl: 2 }}
              display="flex"
              alignItems="end"
              position="relative"
            >
              <ProfileIconCTA onClick={onProfileIconCTAClick} />
            </Stack>
          )}
        </Stack>
        <Stack spacing={0}>
          <Typography
            variant="body1"
            sx={{
              width: "100%",
              fontSize: convertPxToRem(20),
            }}
          >
            {memberProfile && memberProfile?.firstName?.length > 0
              ? t("Home.title.withName", {
                  name: memberProfile.firstName,
                })
              : t("Home.title.withoutName")}
          </Typography>
          <Typography
            variant="h1"
            sx={{
              width: "100%",
              fontSize: convertPxToRem(24),
            }}
          >
            {t("Home.helpSubtitle")}
          </Typography>
        </Stack>
        <Stack sx={{ zIndex: 2, pt: 2 }}>
          <MedicalAssistanceCTA
            memberProfile={memberProfile}
            isLoading={isMemberProfileLoading}
          />
        </Stack>
        {!isFeatureEnabled(FeatureToggleIdentifier.ENABLE_UNIFIED_CARE) && (
          <Stack spacing={2}>
            <Typography component="h2" fontWeight={600}>
              {t("common.myProfile")}
            </Typography>

            <Stack
              direction="row"
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
              }}
              gap={2}
            >
              <HealthCheckTile
                memberProfile={memberProfile}
                isLoading={isMemberProfileLoading}
                shouldShowServiceUsage={true}
              />
              <HealthProfileTile
                lastUpdatedDate={memberProfile?.healthProfile?.lastUpdatedAt}
                healthScore={
                  Number(memberProfile?.healthProfile?.healthScoreValue) || 0
                }
                isLoading={isMemberProfileLoading}
                isError={memberProfileFetchError}
              />
              <BenefitsTile
                benefitCount={memberProfile?.benefitCount}
                isLoading={isMemberProfileLoading}
                isError={memberProfileFetchError}
              />
              <MyPeopleTile
                membershipId={memberProfile?.membership.id}
                dependentsCount={memberProfile?.dependentCount}
                isLoading={isMemberProfileLoading}
                isError={memberProfileFetchError}
              />
            </Stack>

            {isFeatureEnabled(FeatureToggleIdentifier.ENABLE_CHAT_BOT) && (
              <LifestyleAndAdviceSection />
            )}
          </Stack>
        )}
      </Stack>
      <Stack
        spacing={2}
        p={2}
        pt={
          isFeatureEnabled(FeatureToggleIdentifier.ENABLE_UNIFIED_CARE) ? 0 : 2
        }
      >
        {isFeatureEnabled(FeatureToggleIdentifier.ENABLE_TRENDED_VITALS) && (
          <Stack spacing={2}>
            <PersonalizedPanel
              {...getPersonalizedPanelPropsFromProfile(memberProfile)}
            />
          </Stack>
        )}
        <MembershipDashboardPanel
          memberProfile={memberProfile}
          membershipData={{
            firstMembership,
            memberships,
            membershipsFetchError,
            isMembershipsLoading,
          }}
        />
        {isFeatureEnabled(FeatureToggleIdentifier.ENABLE_UNIFIED_CARE) && (
          <AdditionalPayGServicesSection
            memberProfile={memberProfile}
            isMemberProfileLoading={isMemberProfileLoading}
            memberProfileFetchError={memberProfileFetchError}
          />
        )}
      </Stack>
    </Stack>
  );
}
